import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Drawer,
  Box,
  Typography,
  Divider,
  Button,
  IconButton,
  Paper,
} from "@mui/material";
import CartList from "../checkout/CartList";
import { CloseRounded, LocalMallOutlined } from "@mui/icons-material";

export default function CartDrawer({
  open,
  onClose,
  cart,
  t,
  handleRemoveFromCart,
  priceInfo,
  onQuantityChange,
  applyVAT,
  formatPrice,
}) {
  const navigate = useNavigate();
  const cartCount = cart.length;
  const isEmpty = !cart?.length;

  const netSubtotal = useMemo(() => {
    return (cart ?? []).reduce((acc, item) => {
      const itemNet = Number(
        priceInfo?.[item.id]?.totalValue ??
          priceInfo?.[item.id]?.totalExVat ??
          item.pricing?.totalValue ??
          item.pricing?.totalExVat ??
          0,
      );
      return acc + (Number.isFinite(itemNet) ? itemNet : 0);
    }, 0);
  }, [cart, priceInfo]);

  const vatSummary = useMemo(() => {
    if (!applyVAT) {
      return { finalPrice: netSubtotal, vatAmount: 0 };
    }

    return applyVAT(netSubtotal);
  }, [netSubtotal, applyVAT]);

  const overallTotalWithVAT = vatSummary.finalPrice;
  const overallVat = vatSummary.vatAmount;

  const formatCurrency = (value) => {
    const formattedValue = formatPrice ? formatPrice(value) : value;
    return `${formattedValue} kr`;
  };

  const isTotalReady = useMemo(() => {
    if (!cart?.length) return true;
    return cart.every(
      (item) =>
        priceInfo?.[item.id]?.totalValue != null ||
        priceInfo?.[item.id]?.totalExVat != null ||
        item.pricing?.totalValue != null ||
        item.pricing?.totalExVat != null,
    );
  }, [cart, priceInfo]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 450 },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocalMallOutlined color="primary" />

          <Typography variant="h6" fontWeight={500}>
            {t?.("Checkout.Label") ?? "Cart"}
          </Typography>

          <Typography variant="h6" fontWeight={500}>
            ({cartCount})
          </Typography>
        </Box>

        <IconButton onClick={onClose} aria-label="close cart">
          <CloseRounded />
        </IconButton>
      </Box>

      <Divider />

      {/* Content */}
      <Box sx={{ px: 2, py: 2, overflow: "auto", flex: 1 }}>
        {isEmpty ? (
          <Typography variant="body2" color="text.secondary">
            {t?.("Checkout.EmptyCart") ?? "Your cart is empty."}
          </Typography>
        ) : (
          <CartList
            cart={cart}
            handleRemoveFromCart={handleRemoveFromCart}
            t={t}
            priceInfo={priceInfo}
            onQuantityChange={onQuantityChange}
          />
        )}
      </Box>

      <Divider />

      {/* Footer */}
      <Paper
        elevation={0}
        sx={{
          px: 2,
          pt: 2,
          pb: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          backgroundImage: "none",
        }}
      >
        <Box sx={{ mb: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography variant="body2" color="text.subtle">
              {t?.("Checkout.GrandTotal") ?? "Total"}{" "}
              {t?.("Checkout.VATExcluded") ?? "ekskl. mva"}
            </Typography>
            <Typography variant="body2" color="text.subtle" fontWeight={500}>
              {isEmpty
                ? "0 kr"
                : !isTotalReady
                  ? (t?.("Checkout.Calculating") ?? "Beregning...")
                  : formatCurrency(netSubtotal)}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mt: 0.5,
            }}
          >
            <Typography variant="body2" color="text.subtle">
              {t?.("Checkout.VAT") ?? "Mva"}
            </Typography>
            <Typography variant="body2" color="text.subtle" fontWeight={500}>
              {isEmpty
                ? "0 kr"
                : !isTotalReady
                  ? (t?.("Checkout.Calculating") ?? "Beregning...")
                  : `+ ${formatCurrency(overallVat)}`}
            </Typography>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body1" color="text.main" fontWeight={500}>
                {t?.("Checkout.GrandTotal") ?? "Total"}
              </Typography>

              <Typography
                variant="body1"
                fontSize={14}
                color="text.subtle"
                mt={-1}
              >
                {t?.("Checkout.VATIncluded") ?? "inkl. mva"}
              </Typography>
            </Box>

            <Typography variant="h6" fontWeight={500}>
              {isEmpty
                ? "0 kr"
                : !isTotalReady
                  ? (t?.("Checkout.Calculating") ?? "Beregning...")
                  : formatCurrency(overallTotalWithVAT)}
            </Typography>
          </Box>
        </Box>

        <Button
          fullWidth
          variant="contained"
          disabled={isEmpty}
          onClick={() => {
            onClose?.();
            navigate("/checkout");
          }}
          sx={{
            minHeight: 52,
            borderRadius: 1,
            fontWeight: 500,
            textTransform: "none",
          }}
        >
          {t?.("Checkout.Label") ?? "Go to checkout"}
        </Button>

        <Button
          fullWidth
          variant="text"
          onClick={onClose}
          sx={{
            mt: 1,
            minHeight: 44,
            borderRadius: 1,
            textTransform: "none",
          }}
        >
          {t?.("Back") ?? "Continue shopping"}
        </Button>
      </Paper>
    </Drawer>
  );
}
